import React, { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { GhostCta, PrimaryCta, Section, useLanding } from './LandingKit';
import { fluid, isWeb, prefersReducedMotion } from './landingTokens';

const BOTS = [
  {
    name: 'Research assistant', label: 'Research', icon: 'magnifying-glass',
    role: 'Help me understand a topic and make an informed decision.',
    instructions: 'Start with a short summary. Link to original sources. Separate facts from assumptions, and tell me what still needs checking.',
    prompt: 'Compare three booking tools for my small business.',
  },
  {
    name: 'Writing partner', label: 'Writing', icon: 'pen',
    role: 'Help me turn rough notes into clear, useful writing.',
    instructions: 'Keep my voice. Use plain language and short paragraphs. Ask who the reader is before drafting, and avoid sales jargon.',
    prompt: 'Turn these notes into my weekly newsletter.',
  },
  {
    name: 'Coding companion', label: 'Coding', icon: 'code',
    role: 'Help me understand and improve the code I am working on.',
    instructions: 'Explain the tradeoffs before suggesting a change. Follow the project’s conventions. Include a way to check that the fix works.',
    prompt: 'Help me find why this form is not submitting.',
  },
];

function RoleChoice({ bot, active, onPress, c }) {
  const [focused, setFocused] = useState(false);
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel={`Preview ${bot.name}`}
      {...(isWeb ? { 'aria-pressed': active, 'aria-controls': 'landing-team-preview', dataSet: { landingBotChoice: 'true' } } : { accessibilityState: { selected: active } })}
      onPress={onPress}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      style={({ hovered, pressed }) => [styles.choice, {
        borderColor: focused ? c.ink : active ? c.ruleStrong : 'transparent',
        backgroundColor: active ? c.cardSolid : hovered ? c.accentWash : 'transparent',
        opacity: pressed ? 0.76 : 1,
        ...(isWeb ? { cursor: 'pointer', transition: prefersReducedMotion() ? 'none' : 'background-color 180ms ease, border-color 180ms ease' } : {}),
      }]}
    >
      <Text weight={active ? 'semibold' : 'normal'} style={{ color: active ? c.ink : c.muted, fontSize: 14, lineHeight: 20 }}>{bot.label}</Text>
    </Pressable>
  );
}

export default function LandingBots() {
  const { c, width, isXS } = useLanding();
  const compact = width < 1000;
  const [selected, setSelected] = useState(0);
  const bot = BOTS[selected];
  const headingSize = fluid(width, 32, 48);

  return (
    <Section label="Meet Gab AI Bots" background={c.panel} contentStyle={{ paddingVertical: fluid(width, 48, 80) }}>
      <View style={{ flexDirection: compact ? 'column' : 'row', alignItems: compact ? 'stretch' : 'center', gap: compact ? 32 : 80 }}>
        <View style={{ flex: compact ? undefined : 0.85, minWidth: 0 }}>
          <Text weight="medium" style={{ color: c.muted, fontSize: 14, marginBottom: 16 }}>Gab AI Bots</Text>
          <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: headingSize, lineHeight: Math.round(headingSize * 1.12), letterSpacing: -headingSize * 0.035, maxWidth: 420 }}>A familiar helper. Your way of working.</Text>
          <Text tag="p" style={{ color: c.inkSoft, fontSize: 17, lineHeight: 27, marginTop: 20, maxWidth: 420 }}>Save a role, a tone, and instructions in a bot. The next time you need it, your preferences are already there.</Text>
          <Text style={{ color: c.inkSoft, fontSize: 15, lineHeight: 24, marginTop: 16, maxWidth: 420 }}>Create your own or add a community bot to your sidebar.</Text>
          <View style={{ marginTop: 24, alignItems: 'flex-start', gap: 4 }}>
            <PrimaryCta restrained label="Explore bots" href="/agents" full={isXS} />
            <GhostCta label="How bots work" href="/docs/agents" />
          </View>
        </View>

        <View style={{ flex: compact ? undefined : 1.15, minWidth: 0 }}>
          <View style={{ borderRadius: 16, borderWidth: 1, borderColor: c.rule, backgroundColor: c.cardSolid, overflow: 'hidden' }}>
            <View accessibilityRole={isWeb ? 'group' : undefined} accessibilityLabel="Choose a bot to preview" style={{ flexDirection: 'row', padding: 8, gap: 4, backgroundColor: c.panel, borderBottomWidth: 1, borderBottomColor: c.rule }}>
              {BOTS.map((item, index) => <RoleChoice key={item.name} bot={item} active={selected === index} onPress={() => setSelected(index)} c={c} />)}
            </View>
            <View nativeID="landing-team-preview" accessibilityLabel={`${bot.name} saved instructions example`} style={{ padding: isXS ? 20 : 32 }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 28 }}>
                <View aria-hidden={isWeb ? true : undefined} style={{ width: 40, height: 40, borderRadius: 10, backgroundColor: c.panel, alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name={bot.icon} size={18} color={c.ink} />
                </View>
                <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: 20, lineHeight: 28, letterSpacing: -0.4, flex: 1 }}>{bot.name}</Text>
              </View>
              <Text weight="medium" style={{ color: c.muted, fontSize: 13, marginBottom: 8 }}>Role</Text>
              <Text style={{ color: c.ink, fontSize: 15, lineHeight: 24 }}>{bot.role}</Text>
              <View style={{ marginTop: 24, paddingTop: 24, borderTopWidth: 1, borderTopColor: c.rule }}>
                <Text weight="medium" style={{ color: c.muted, fontSize: 13, marginBottom: 8 }}>Saved instructions</Text>
                <Text style={{ color: c.ink, fontSize: 15, lineHeight: 25 }}>{bot.instructions}</Text>
              </View>
              <View style={{ marginTop: 24, padding: 16, borderRadius: 10, backgroundColor: c.panel, gap: 8 }}>
                <Text weight="medium" style={{ color: c.muted, fontSize: 13 }}>Your next conversation</Text>
                <Text style={{ color: c.ink, fontSize: 15, lineHeight: 24 }}>{`“${bot.prompt}”`}</Text>
              </View>
            </View>
          </View>
          <Text style={{ color: c.muted, fontSize: 12, lineHeight: 20, marginTop: 12 }}>Example configuration · Choose a role to see different instructions.</Text>
        </View>
      </View>
      <Text {...(isWeb ? { 'aria-live': 'polite', 'aria-atomic': true } : { accessibilityLiveRegion: 'polite' })} style={styles.announcement}>{`${bot.name} example selected. ${bot.instructions}`}</Text>
    </Section>
  );
}

const styles = StyleSheet.create({
  choice: { flex: 1, minWidth: 0, minHeight: 44, paddingHorizontal: 8, paddingVertical: 12, borderRadius: 10, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  announcement: { position: 'absolute', width: 1, height: 1, overflow: 'hidden', ...(isWeb ? { clipPath: 'inset(50%)', whiteSpace: 'nowrap' } : {}) },
});
